import { useState } from "react";
import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { Plus, Sparkles, Pencil, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient } from "@/lib/queryClient";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getExperienceImageUrl, getBackendBase } from "@/environments/environment";

interface Experience {
    id: string;
    title: string;
    description: string;
    location: string;
    price: number;
    duration: string;
    category: string;
    maxParticipants: number;
    rating: number;
    reviews: number;
    imageUrl?: string;
    isActive: boolean;
    createdAt: string;
}

export default function AdminExperiencePage() {
    const { hasPermission } = useAuth() as any;
    const { toast } = useToast();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingExperience, setEditingExperience] = useState<Experience | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Experience | null>(null);

    // API base for admin endpoints (matching GroupTours pattern)
    const backendBase = getBackendBase();
    const adminEndpoint = `${backendBase}/v1/admin/experiences`;

    const { data: experiences = [], isLoading } = useQuery<Experience[]>({
        queryKey: [adminEndpoint],
        queryFn: async () => {
            try {
                // compute base and public endpoint
                const publicEndpoint = `${backendBase}/v1/experiences`;

                // attempt admin fetch (requires admin token)
                const response = await fetch(adminEndpoint, {
                    headers: {
                        "Authorization": `Bearer ${localStorage.getItem("admin_token")}`,
                    },
                });

                if (response.ok) {
                    const result = await response.json();
                    return result.results || result.data || result || [];
                }

                // If unauthorized or forbidden, fall back to public experiences endpoint
                if (response.status === 401 || response.status === 403) {
                    try {
                        const pubRes = await fetch(publicEndpoint);
                        if (!pubRes.ok) throw new Error('Failed to load public experiences');
                        const pubResult = await pubRes.json();
                        return pubResult.results || pubResult.data || pubResult || [];
                    } catch (err) {
                        // fall through to toast below
                    }
                }

                // for other non-ok responses, throw to be handled below
                throw new Error(`Failed to load experiences (status ${response.status})`);
            } catch (error) {
                toast({
                    title: "Failed to load experiences",
                    description: error instanceof Error ? error.message : "Unknown error",
                    variant: "destructive",
                });
                return [];
            }
        },
    });

    const canWrite = hasPermission("experiences", "write");

    const createMutation = useMutation({
        mutationFn: async (data: FormData) => {
            const response = await fetch(adminEndpoint, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("admin_token")}`,
                },
                body: data,
            });
            if (!response.ok) throw new Error("Failed to create experience");
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [adminEndpoint] });
            toast({ title: "Experience created successfully" });
            setDialogOpen(false);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to create experience",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: FormData }) => {
            const response = await fetch(`${adminEndpoint}/${id}`, {
                method: "PATCH",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("admin_token")}`,
                },
                body: data,
            });
            if (!response.ok) throw new Error("Failed to update experience");
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [adminEndpoint] });
            toast({ title: "Experience updated successfully" });
            setDialogOpen(false);
            setEditingExperience(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to update experience",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const response = await fetch(`${adminEndpoint}/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("admin_token")}`,
                },
            });
            if (!response.ok) throw new Error("Failed to delete experience");
            // Backend returns 204 No Content on success; handle empty body gracefully
            try {
                return await response.json();
            } catch (e) {
                return null;
            }
        },
        onSuccess: (_data, variables) => {
            const deletedId = variables as string;
            // Optimistically remove the deleted item from cache
            try {
                queryClient.setQueryData([adminEndpoint], (old: any) => {
                    if (!old) return old;
                    if (Array.isArray(old)) {
                        return old.filter((it: any) => (it.id || it._id) !== deletedId);
                    }
                    if (old.results && Array.isArray(old.results)) {
                        return { ...old, results: old.results.filter((it: any) => (it.id || it._id) !== deletedId) };
                    }
                    return old;
                });
            } catch (e) {
                // ignore
            }

            queryClient.invalidateQueries({ queryKey: [adminEndpoint] });
            toast({ title: "Experience deleted successfully" });
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to delete experience",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);

        const data = new FormData();
        data.append("title", formData.get("title") as string);
        data.append("description", formData.get("description") as string);
        data.append("location", formData.get("location") as string);
        data.append("category", formData.get("category") as string);
        data.append("price", formData.get("price") as string);
        data.append("duration", formData.get("duration") as string);
        data.append("maxParticipants", formData.get("maxParticipants") as string);
        data.append("rating", formData.get("rating") as string);
        data.append("reviews", formData.get("reviews") as string);
        data.append("isActive", formData.get("isActive") === "on" ? "true" : "false");

        // Add image file if provided
        const imageFile = formData.get("image") as File;
        if (imageFile && imageFile.size > 0) {
            data.append("image", imageFile);
        }

        // Prefer explicit hidden _id field from the form (safer), then fallback to editingExperience
        const formId = (formData.get("_id") as string) || null;

        if (formId) {
            updateMutation.mutate({ id: formId, data });
        } else if (editingExperience) {
            updateMutation.mutate({ id: editingExperience.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Experiences</h1>
                    <p className="text-muted-foreground mt-2">Manage travel experiences and activities</p>
                </div>
                {canWrite && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setEditingExperience(null)} disabled={createMutation.isPending}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Experience
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editingExperience ? "Edit Experience" : "Create New Experience"}</DialogTitle>
                                <DialogDescription>{editingExperience ? "Update experience information" : "Add a new travel experience"}</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* hidden id so submit can reliably detect update vs create */}
                                <input type="hidden" name="_id" value={editingExperience?.id ?? ""} />

                                <div className="space-y-2">
                                    <Label htmlFor="title">Experience Title *</Label>
                                    <Input id="title" name="title" defaultValue={editingExperience?.title} required />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea id="description" name="description" defaultValue={editingExperience?.description} rows={3} />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="location">Location *</Label>
                                        <Input id="location" name="location" defaultValue={editingExperience?.location} required />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="category">Category *</Label>
                                        <Input id="category" name="category" placeholder="e.g., Adventure, Cultural" defaultValue={editingExperience?.category} required />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="price">Price (₹) *</Label>
                                        <Input id="price" name="price" type="number" step="1" defaultValue={editingExperience?.price} required />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="duration">Duration *</Label>
                                        <Input id="duration" name="duration" placeholder="e.g., 2-3 hours, 1 day" defaultValue={editingExperience?.duration} required />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="maxParticipants">Max Participants *</Label>
                                        <Input id="maxParticipants" name="maxParticipants" type="number" defaultValue={editingExperience?.maxParticipants} required />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="rating">Rating (0-5) *</Label>
                                        <Input id="rating" name="rating" type="number" step="0.1" min="0" max="5" defaultValue={editingExperience?.rating || 0} required />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="reviews">Number of Reviews *</Label>
                                    <Input id="reviews" name="reviews" type="number" defaultValue={editingExperience?.reviews || 0} required />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="image">Image (JPG/PNG)</Label>
                                    <Input id="image" name="image" type="file" accept="image/*" />
                                    {editingExperience?.imageUrl && (
                                        <p className="text-sm text-muted-foreground">Current image: {editingExperience.imageUrl}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <input type="checkbox" name="isActive" defaultChecked={editingExperience?.isActive !== false} />
                                        <span>Active</span>
                                    </Label>
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingExperience(null); }}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                                        {editingExperience ? "Update" : "Create"} Experience
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <Card>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-6 space-y-4">{[1, 2, 3].map((i) => (<Skeleton key={i} className="h-16 w-full" />))}</div>
                    ) : experiences && experiences.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Location</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead>Rating</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {experiences.map((experience) => (
                                    <TableRow key={experience.id}>
                                        <TableCell>
                                            <div className="flex items-start gap-2">
                                                {experience.imageUrl ? (
                                                    <img
                                                        src={getExperienceImageUrl(experience.imageUrl)}
                                                        alt={experience.title}
                                                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <Sparkles className="w-12 h-12 p-2 rounded bg-muted text-muted-foreground flex-shrink-0" />
                                                )}
                                                <div>
                                                    <p className="font-medium">{experience.title}</p>
                                                    <p className="text-xs text-muted-foreground">{experience.duration}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>{experience.location}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{experience.category}</Badge>
                                        </TableCell>
                                        <TableCell className="font-medium">₹{experience.price.toLocaleString()}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <span className="text-yellow-500">★</span>
                                                <span>{experience.rating}</span>
                                                <span className="text-xs text-muted-foreground">({experience.reviews})</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={experience.isActive ? "default" : "secondary"}>
                                                {experience.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </TableCell>
                                        {canWrite && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="ghost" size="icon" onClick={() => { setEditingExperience(experience); setTimeout(() => setDialogOpen(true), 0); }} disabled={updateMutation.isPending}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setDeleteTarget(experience);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        disabled={deleteMutation.isPending}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-semibold">No experiences yet</h3>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm">Create and manage travel experiences and activities.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete experience</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete <strong>{deleteTarget?.title}</strong>? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}>Cancel</Button>
                        <Button variant="destructive" onClick={() => {
                            if (deleteTarget) {
                                deleteMutation.mutate(deleteTarget.id);
                            }
                            setDeleteDialogOpen(false);
                            setDeleteTarget(null);
                        }} disabled={deleteMutation.isPending}>Delete</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
